import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import {
  ShieldAlert, RefreshCw, Loader, Plus, Trash2, Save, Sparkles, Languages,
  Phone, MapPin, CheckCircle, AlertTriangle, Radio, Users, FileUp, X, Eye, Rocket, BookOpen,
  Image as ImageIcon, ArrowLeft, ArrowRight, Upload, FileSpreadsheet, FileText, LayoutGrid, List, Send,
  Smartphone,
} from 'lucide-react';
import { SafetyBriefGate } from '../components/SafetyPortal';
import SecurityDeclarationTab from './SecurityDeclarationTab';
import IncidentReportsTab from './IncidentReportsTab';
import { uprightJpegBytes } from '../utils/imageOrient';
import { downloadAwarenessXlsx, downloadAwarenessPdf } from '../utils/awarenessReport';

// Safety — the engine half of the safety portal.
//
// Five things live here, and they are deliberately one page because they are one
// loop: the PLAN says what we do, a TOPIC is how that reaches a driver, the FOCUS
// is which topic is on their home screen this week, a REPORT is what comes back,
// and a HAZARD is the standing instruction that report turns into for everyone else.
// AWARENESS is the evidence that the loop closed.
//
// Backed by safety-sb (settings KV — no migration). Photos are served as short-lived
// signed URLs minted by the backend at list time.

const URL = '/.netlify/functions/safety-sb';
const api = (action, payload = {}) =>
  fetch(URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) }).then(r => r.json());

const labelStyle = { display: 'block', fontSize: '.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--hp-dim)', marginBottom: 4 };
const inputStyle = { width: '100%', boxSizing: 'border-box' };
const th = { textAlign: 'left', padding: '9px 11px', fontSize: '.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--hp-dim)', borderBottom: '1px solid var(--hp-border)' };
const td = { padding: '9px 11px', fontSize: '.83rem', borderBottom: '1px solid var(--hp-border)', verticalAlign: 'top' };

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(`${d}T00:00:00`);
  return isNaN(dt) ? d : dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');

const STATUS_COLOR = { new: '#ef4444', reviewing: '#d97706', actioned: '#2563eb', closed: '#16a34a' };
const TONE_COLOR = { red: '#ef4444', amber: '#d97706', green: '#16a34a' };

const Chip = ({ text, color }) => (
  <span style={{ fontSize: '.66rem', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.05em', color, border: `1px solid ${color}`, borderRadius: 5, padding: '2px 6px', whiteSpace: 'nowrap' }}>{text}</span>
);

export default function SafetyPage({ initialTab }) {
  const { addNotification } = useApp();
  const notify = useCallback((m, k) => addNotification?.(m, k), [addNotification]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // The tab is addressable: /pre-card lands on the Pre-Card register, and ?tab= lets the
  // search palette and the Customer Cases banner link straight to it.
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(initialTab || params.get('tab') || 'plan');
  const openTab = useCallback((k) => {
    setTab(k);
    setParams(prev => { const n = new URLSearchParams(prev); n.set('tab', k); return n; }, { replace: true });
  }, [setParams]);
  const [acks, setAcks] = useState(null);
  const [hazardAcks, setHazardAcks] = useState(null);
  const [people, setPeople] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api('admin');
    if (r.success) setData(r); else notify(r.error || 'Failed to load safety data', 'error');
    setLoading(false);
  }, [notify]);
  useEffect(() => { load(); }, [load]);

  const newReports = useMemo(() => ((data && data.reports) || []).filter(r => r.status === 'new').length, [data]);
  const liveHazards = useMemo(() => ((data && data.hazards) || []).filter(h => h.active !== false).length, [data]);
  const precardCount = useMemo(() => ((data && data.hazards) || []).filter(h => isPrecard(h) && h.active !== false).length, [data]);
  // A Pre-Card point Australia Post has not been told about yet. Said on the page header
  // because it is an obligation with a clock on it, not a to-do.
  const precardUnadvised = useMemo(() => ((data && data.hazards) || []).filter(h => isPrecard(h) && h.active !== false && !h.ap_notified).length, [data]);

  // The plan is "due" if it has never been marked reviewed here, or the review
  // interval has lapsed. The old plan's last touch was 2024 — say so out loud.
  const reviewState = useMemo(() => {
    const p = (data && data.plan) || {};
    if (!p.reviewed_at) return { due: true, text: `Never reviewed in the engine · last touched ${p.previous_review || '2024'}` };
    const due = new Date(p.reviewed_at);
    due.setMonth(due.getMonth() + (p.review_months || 12));
    const overdue = due < new Date();
    return { due: overdue, text: `Reviewed ${fmtDate(p.reviewed_at)}${p.reviewed_by ? ` by ${p.reviewed_by}` : ''} · next due ${fmtDate(due.toISOString().slice(0, 10))}` };
  }, [data]);

  // A scoped team leader gets the reports for their own area, the hazard register they
  // investigate into, their team's briefing evidence, and the plan to read. The plan
  // itself, the topic library and the focus stay with the office.
  const scope = (data && data.scope) || { isAdmin: true };
  const TABS = [
    ['plan', 'Safety Plan'],
    ...(scope.isAdmin ? [['topics', `Topics (${((data && data.topics) || []).filter(t => t.status !== 'archived').length})`]] : []),
    ['reports', `Reports${newReports ? ` · ${newReports} new` : ''}`],
    // The formal write-up that goes to Australia Post. Office only: it carries a driver's
    // injury and treatment. Own store and gate — see IncidentReportsTab.
    ...(scope.isAdmin ? [['incidents', 'Incident Reports']] : []),
    ['precard', `Pre-Card${precardCount ? ` · ${precardCount}` : ''}`],
    ['hazards', `Hazard Register${liveHazards ? ` · ${liveHazards}` : ''}`],
    ['awareness', 'Awareness'],
    // Security is not safety — it is Australia Post's signed declaration, with its own
    // store and its own gate. It sits here because it is the same loop: issued by the
    // office, read on a phone, evidenced back. See SecurityDeclarationTab.
    ['security', 'Security Declaration'],
  ];

  const openAwareness = async () => {
    openTab('awareness');
    if (acks) return;
    const r = await api('ackReport');
    if (r.success) { setAcks(r.acks || []); setHazardAcks(r.hazardAcks || []); setPeople(r.people || {}); }
    else notify(r.error || 'Failed to load', 'error');
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ShieldAlert size={22} style={{ color: 'var(--hp-gold)' }} /> Safety
          </h2>
          <p>
            {scope.isAdmin
              ? "The WHS plan, what reaches the drivers' phones, and what comes back"
              : `Your area — ${(scope.areas || []).join(', ')}`}
            {newReports > 0 && <span style={{ color: '#ef4444', fontWeight: 700 }}> · {newReports} new report{newReports > 1 ? 's' : ''}</span>}
            {liveHazards > 0 && <span style={{ color: '#d97706', fontWeight: 700 }}> · {liveHazards} live hazard{liveHazards > 1 ? 's' : ''}</span>}
            {precardUnadvised > 0 && <span style={{ color: '#dc2626', fontWeight: 700 }}> · {precardUnadvised} Pre-Card point{precardUnadvised > 1 ? 's' : ''} not advised to AP</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={load} title="Refresh"><RefreshCw size={14} /></button>
        </div>
      </div>

      <div className="page-body">
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
          {TABS.map(([k, label]) => (
            <button key={k} className={`btn btn-sm ${tab === k ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => (k === 'awareness' ? openAwareness() : openTab(k))}>{label}</button>
          ))}
        </div>

        {loading && <div style={{ padding: 40, textAlign: 'center', color: 'var(--hp-dim)' }}><Loader size={18} /> Loading…</div>}

        {!loading && data && (
          <>
            {tab === 'plan' && <PlanTab data={data} reviewState={reviewState} onSaved={load} notify={notify} readOnly={!scope.isAdmin} />}
            {tab === 'topics' && scope.isAdmin && <TopicsTab data={data} onSaved={load} notify={notify} />}
            {tab === 'reports' && <ReportsTab data={data} onSaved={load} notify={notify} scope={scope} />}
            {tab === 'precard' && <PrecardTab data={data} onSaved={load} notify={notify} scope={scope} />}
            {tab === 'hazards' && <HazardsTab data={data} onSaved={load} notify={notify} scope={scope} />}
            {tab === 'awareness' && <AwarenessTab acks={acks} hazardAcks={hazardAcks} people={people} topics={data.topics} langs={data.langs} notify={notify} />}
            {tab === 'security' && <SecurityDeclarationTab notify={notify} />}
            {tab === 'incidents' && scope.isAdmin && <IncidentReportsTab notify={notify} />}
          </>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Safety Plan
// ══════════════════════════════════════════════════════════════════════════════
function PlanTab({ data, reviewState, onSaved, notify, readOnly }) {
  const [plan, setPlan] = useState(data.plan);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  // A team leader reads the plan; they do not edit it. Every field below is disabled
  // rather than hidden, so a leader can still see exactly what their drivers are told.
  const set = (patch) => { if (readOnly) return; setPlan(p => ({ ...p, ...patch })); setDirty(true); };
  const setContact = (i, patch) => set({ contacts: plan.contacts.map((c, j) => (j === i ? { ...c, ...patch } : c)) });

  const save = async () => {
    setBusy(true);
    const r = await api('savePlan', { plan });
    setBusy(false);
    if (r.success) { setDirty(false); notify('Safety plan saved', 'success'); onSaved(); }
    else notify(r.error || 'Failed to save', 'error');
  };

  const markReviewed = async () => {
    const r = await api('markReviewed');
    if (r.success) { setPlan(r.plan); notify('Plan marked as reviewed today', 'success'); onSaved(); }
    else notify(r.error || 'Failed', 'error');
  };

  const missingNumbers = (plan.contacts || []).filter(c => !c.phone).length;

  return (
    <fieldset disabled={!!readOnly} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
      {/* Review state — the honest banner. The old plan sat untouched since 2024. */}
      <div className="card" style={{ padding: 14, marginBottom: 16, borderLeft: `4px solid ${reviewState.due ? '#ef4444' : '#16a34a'}`, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {reviewState.due ? <AlertTriangle size={18} color="#ef4444" /> : <CheckCircle size={18} color="#16a34a" />}
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontWeight: 800, fontSize: '.9rem' }}>{reviewState.due ? 'Review overdue' : 'Review current'}</div>
          <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)', marginTop: 2 }}>{reviewState.text}</div>
        </div>
        {!readOnly && <button className="btn btn-secondary btn-sm" onClick={markReviewed}><CheckCircle size={14} /> Mark reviewed today</button>}
      </div>

      {missingNumbers > 0 && !readOnly && (
        <div className="card" style={{ padding: 12, marginBottom: 16, borderLeft: '4px solid #d97706', fontSize: '.83rem' }}>
          <strong>{missingNumbers} contact{missingNumbers > 1 ? 's have' : ' has'} no phone number.</strong> A contact with no number is
          hidden from the driver portal — the "who to call" list is the part of this plan drivers actually use on the road.
        </div>
      )}

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <label style={labelStyle}>Our commitment — shown at the top of the driver's Safety tab</label>
        <textarea className="input" style={{ ...inputStyle, minHeight: 80 }} value={plan.commitment || ''}
          onChange={e => set({ commitment: e.target.value })} />
      </div>

      {/* Contacts — the highest-value block on the page */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Phone size={16} color="var(--hp-gold)" />
          <div style={{ fontWeight: 800 }}>Who to call</div>
          <div style={{ fontSize: '.76rem', color: 'var(--hp-dim)' }}>Tap-to-call on the driver's phone</div>
          <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }}
            onClick={() => set({ contacts: [...(plan.contacts || []), { id: `c_${Date.now()}`, role: '', name: '', phone: '', when: '' }] })}>
            <Plus size={13} /> Add
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {(plan.contacts || []).map((c, i) => (
            <div key={c.id || i} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start', paddingBottom: 10, borderBottom: '1px solid var(--hp-border)' }}>
              <div style={{ flex: '1 1 160px' }}>
                <label style={labelStyle}>Role</label>
                <input className="input" style={inputStyle} value={c.role || ''} onChange={e => setContact(i, { role: e.target.value })} />
              </div>
              <div style={{ flex: '1 1 150px' }}>
                <label style={labelStyle}>Name</label>
                <input className="input" style={inputStyle} value={c.name || ''} onChange={e => setContact(i, { name: e.target.value })} />
              </div>
              <div style={{ flex: '0 1 140px' }}>
                <label style={labelStyle}>Phone</label>
                <input className="input" style={{ ...inputStyle, borderColor: c.phone ? undefined : '#d97706' }} value={c.phone || ''}
                  placeholder="not set" onChange={e => setContact(i, { phone: e.target.value })} />
              </div>
              <div style={{ flex: '2 1 260px' }}>
                <label style={labelStyle}>When to call</label>
                <input className="input" style={inputStyle} value={c.when || ''} onChange={e => setContact(i, { when: e.target.value })} />
              </div>
              <button className="btn btn-secondary btn-sm" style={{ marginTop: 18 }}
                onClick={() => set({ contacts: plan.contacts.filter((_, j) => j !== i) })} title="Remove">
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* If it goes wrong */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ fontWeight: 800, marginBottom: 10 }}>If it goes wrong — the four steps</div>
        {(plan.if_it_goes_wrong || []).map((s, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
            <span style={{ width: 22, height: 22, borderRadius: 999, background: 'var(--hp-gold)', color: '#1a1a2e', fontWeight: 900, fontSize: '.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 6, flexShrink: 0 }}>{i + 1}</span>
            <input className="input" style={{ flex: '0 1 160px' }} value={s.step}
              onChange={e => set({ if_it_goes_wrong: plan.if_it_goes_wrong.map((x, j) => (j === i ? { ...x, step: e.target.value } : x)) })} />
            <input className="input" style={{ flex: 1 }} value={s.body}
              onChange={e => set({ if_it_goes_wrong: plan.if_it_goes_wrong.map((x, j) => (j === i ? { ...x, body: e.target.value } : x)) })} />
          </div>
        ))}
      </div>

      {/* Responsibilities */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
        {[['us', 'What we do'], ['you', 'What you do']].map(([k, title]) => (
          <div key={k} className="card" style={{ padding: 16, flex: '1 1 320px' }}>
            <div style={{ fontWeight: 800, marginBottom: 10 }}>{title}</div>
            <textarea className="input" style={{ ...inputStyle, minHeight: 140 }}
              value={((plan.responsibilities || {})[k] || []).join('\n')}
              onChange={e => set({ responsibilities: { ...plan.responsibilities, [k]: e.target.value.split('\n').filter(Boolean) } })} />
            <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginTop: 6 }}>One per line</div>
          </div>
        ))}
      </div>

      {/* Hazard & risk register — kept in AP's audit language */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <div style={{ fontWeight: 800 }}>Hazard &amp; risk register</div>
          <div style={{ fontSize: '.76rem', color: 'var(--hp-dim)' }}>The AP contractor-safety-review view · each row links to the topic drivers read</div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
            <thead><tr><th style={th}>Hazard</th><th style={th}>Controls</th><th style={th}>Who</th><th style={th}>Topic</th></tr></thead>
            <tbody>
              {(plan.register || []).map((r, i) => (
                <tr key={i}>
                  <td style={{ ...td, fontWeight: 700, width: 180 }}>{r.hazard}</td>
                  <td style={{ ...td, color: 'var(--hp-dim)' }}>{r.controls}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.who}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {r.topic ? <Chip text={r.topic} color="var(--hp-gold)" /> : <span style={{ color: 'var(--hp-dim)' }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Systems */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ fontWeight: 800, marginBottom: 10 }}>How the plan stays alive</div>
        {(plan.systems || []).map((s, i) => (
          <div key={i} style={{ marginBottom: 10 }}>
            <div style={{ fontWeight: 700, fontSize: '.85rem' }}>{s.title}</div>
            <textarea className="input" style={{ ...inputStyle, minHeight: 54, marginTop: 4 }} value={s.body}
              onChange={e => set({ systems: plan.systems.map((x, j) => (j === i ? { ...x, body: e.target.value } : x)) })} />
          </div>
        ))}
      </div>

      {!readOnly && (
        <div style={{ position: 'sticky', bottom: 0, background: 'var(--hp-bg, #101014)', padding: '12px 0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-primary" disabled={!dirty || busy} onClick={save}>
            <Save size={14} /> {busy ? 'Saving…' : dirty ? 'Save plan' : 'Saved'}
          </button>
        </div>
      )}
    </fieldset>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Topics
// ══════════════════════════════════════════════════════════════════════════════
function TopicsTab({ data, onSaved, notify }) {
  const [editing, setEditing] = useState(null);   // topic object being edited
  const [genOpen, setGenOpen] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [previewing, setPreviewing] = useState(null);
  const focusId = (data.focus && data.focus.topicId) || '';

  const topics = (data.topics || []).filter(t => t.status !== 'archived');
  const byId = Object.fromEntries((data.briefStatus || []).map(b => [b.id, b]));
  // How many driver sign-offs are currently held up, and by how many live briefs.
  const compulsoryCount = (data.briefStatus || []).length;
  const stillOwed = new Set((data.briefStatus || []).flatMap(b => b.outstanding)).size;

  const setFocus = async (topicId) => {
    const note = window.prompt('Add a one-line note for the home screen (optional) — e.g. "Two bites in the network this month."', '');
    if (note === null) return;
    const r = await api('setFocus', { topicId, note });
    if (r.success) { notify('Safety focus updated — it is on every driver’s home screen now', 'success'); onSaved(); }
    else notify(r.error || 'Failed', 'error');
  };

  // LAUNCH — the action that makes a brief compulsory. Worth a confirm: from the moment
  // it lands, nobody signs off a shift until they have read it.
  const launch = async (t) => {
    const already = !!t.launched_at && t.requireAck === true;
    const msg = already
      ? `Re-issue "${t.title}"?\n\nEveryone will have to read it AGAIN before their next scanner sign-off, including drivers who already read it.`
      : `Launch "${t.title}" to the drivers?\n\nIt goes on every home screen, and no driver can submit a shift until they have read it.`;
    if (!window.confirm(msg)) return;
    const note = window.prompt('One-line note for the home screen (optional) — e.g. "Two bites in the network this month."', t.launchNote || '');
    if (note === null) return;
    setBusyId(t.id);
    const r = await api('launchTopic', { id: t.id, note });
    setBusyId('');
    if (r.success) { notify(r.reissue ? 'Re-issued — everyone reads it again' : 'Launched — it now gates every sign-off until read', 'success'); onSaved(); }
    else notify(r.error || 'Failed to launch', 'error');
  };

  // See the banner before you spend 54 people's attention on it. Launching is the only
  // thing that fires the real alert, so without this the only way to find out whether
  // alerting works at all was to interrupt everybody.
  const testAlert = async (t) => {
    setBusyId(t.id);
    const r = await api('testBriefAlert', { id: t.id });
    setBusyId('');
    if (r.success) notify(`Sent to your phone — ${r.sent} device${r.sent === 1 ? '' : 's'}. Nobody else was alerted.`, 'success');
    else notify(r.error || 'Could not send the alert', 'error');
  };

  const standDown = async (t) => {
    if (!window.confirm(`Stop "${t.title}" blocking sign-off?\n\nIt stays on the drivers' shelf to read, it just stops being compulsory.`)) return;
    const r = await api('standDownTopic', { id: t.id });
    if (r.success) { notify('No longer compulsory', 'success'); onSaved(); }
    else notify(r.error || 'Failed', 'error');
  };

  const translate = async (t) => {
    setBusyId(t.id);
    const r = await api('translateTopic', { id: t.id });
    setBusyId('');
    if (r.success) { notify(`Translated into ${r.done.join(', ').toUpperCase()}${r.failed.length ? ` (failed: ${r.failed.join('; ')})` : ''}`, 'success'); onSaved(); }
    else notify(r.error || 'Translation failed', 'error');
  };

  const remove = async (t) => {
    if (!window.confirm(`Remove "${t.title}" from the topic library?`)) return;
    const r = await api('deleteTopic', { id: t.id });
    if (r.success) { notify('Topic removed', 'success'); onSaved(); } else notify(r.error || 'Failed', 'error');
  };

  return (
    <div>
      <div className="card" style={{ padding: 14, marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <Radio size={16} color="var(--hp-gold)" />
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontWeight: 800, fontSize: '.9rem' }}>
            Current safety focus: {focusId ? (topics.find(t => t.id === focusId) || {}).title || focusId : 'none'}
          </div>
          <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)', marginTop: 2 }}>
            {data.focus && data.focus.since ? `Set ${fmtDate(data.focus.since)}` : ''}
            {data.focus && data.focus.note ? ` · “${data.focus.note}”` : ''}
            {' · '}<strong style={{ color: 'var(--hp-text)' }}>Launch to drivers</strong> makes a brief compulsory: it goes on
            every home screen AND blocks their scanner sign-off until they have read it to the end. Launch them one at a time —
            a driver handed six briefs at the end of a long day reads none of them.
          </div>
          {stillOwed > 0 && (
            <div style={{ fontSize: '.8rem', color: '#d97706', fontWeight: 700, marginTop: 5 }}>
              {stillOwed} sign-off{stillOwed > 1 ? 's' : ''} currently blocked across {compulsoryCount} live brief{compulsoryCount > 1 ? 's' : ''}.
            </div>
          )}
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => setGenOpen(true)}><FileUp size={14} /> New topic from a document</button>
        <button className="btn btn-secondary btn-sm" onClick={() => setEditing({ title: '', subtitle: '', category: 'High Risk', emoji: '⚠️', accent: '#c9a84c', status: 'draft', showToDrivers: false, sections: defaultSections() })}>
          <Plus size={14} /> Blank topic
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(330px, 100%), 1fr))', gap: 14 }}>
        {topics.map(t => {
          const langs = ['en', ...Object.keys(t.i18n || {})];
          const isFocus = t.id === focusId;
          const compulsory = t.requireAck === true && t.status === 'published' && t.showToDrivers !== false;
          const st = byId[t.id];
          return (
            <div key={t.id} className="card" style={{ padding: 14, borderLeft: `4px solid ${t.accent || 'var(--hp-gold)'}` }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
                <span style={{ fontSize: '1.5rem', lineHeight: 1 }}>{t.emoji}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: '.95rem' }}>{t.title}</div>
                  <div style={{ fontSize: '.76rem', color: 'var(--hp-dim)', marginTop: 2, lineHeight: 1.4 }}>{t.subtitle}</div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                {compulsory && <Chip text="Must read" color="#ef4444" />}
                {isFocus && <Chip text="On home screens" color="#16a34a" />}
                <Chip text={t.category} color={t.category === 'Critical Risk' ? '#ef4444' : '#d97706'} />
                <Chip text={t.status === 'published' ? 'Published' : 'Draft'} color={t.status === 'published' ? '#2563eb' : 'var(--hp-dim)'} />
                <Chip text={langs.join(' ').toUpperCase()} color="var(--hp-gold)" />
              </div>

              <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)', marginTop: 8 }}>
                {(t.sections || []).map(s => `${s.label}: ${(s.items || []).length}`).join(' · ')}
              </div>

              {/* Launch state — read counts against the live roster. */}
              {compulsory && st && (
                <div style={{ marginTop: 10, fontSize: '.78rem' }}>
                  <div style={{ fontWeight: 700, color: st.outstanding.length ? '#d97706' : '#16a34a' }}>
                    {st.read.length}/{st.rosterSize} read
                    {st.outstanding.length ? ` · ${st.outstanding.length} still to sign off` : ' · everyone done'}
                  </div>
                  {st.outstanding.length > 0 && (
                    <div style={{ color: 'var(--hp-dim)', marginTop: 3, fontFamily: 'var(--font-mono)', fontSize: '.7rem', lineHeight: 1.5 }}>
                      {st.outstanding.slice(0, 10).join(', ')}{st.outstanding.length > 10 ? ` +${st.outstanding.length - 10}` : ''}
                    </div>
                  )}
                  {t.launched_at && (
                    <div style={{ color: 'var(--hp-dim)', marginTop: 3 }}>
                      Launched {fmtWhen(t.launched_at)}{t.launch_count > 1 ? ` · issue ${t.launch_count}` : ''}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setEditing(t)}>Edit</button>
                {/* Read it as a driver will, before making 50-odd people read it. */}
                <button className="btn btn-secondary btn-sm" onClick={() => setPreviewing({ topic: t })}>
                  <Eye size={13} /> Preview
                </button>
                {/* Exactly what the driver opens from the topic — the illustrated guide with
                    its animations, on the same URL their phone hits. Opened in a new tab so
                    the register keeps its place. */}
                {t.guideUrl && (
                  <a className="btn btn-secondary btn-sm" href={t.guideUrl} target="_blank" rel="noopener noreferrer"
                    title={`Open ${t.guideUrl} — the driver's illustrated guide`}>
                    <BookOpen size={13} /> View guide
                  </a>
                )}
                {t.status === 'published' && (
                  <button className="btn btn-primary btn-sm" disabled={busyId === t.id} onClick={() => launch(t)}>
                    <Rocket size={13} /> {compulsory ? 'Re-issue' : 'Launch to drivers'}
                  </button>
                )}
                {t.status === 'published' && (
                  <button className="btn btn-secondary btn-sm" disabled={busyId === t.id}
                    onClick={() => testAlert(t)}
                    title="Send this brief's banner to your own phone. No launch, nobody else alerted.">
                    <Smartphone size={13} /> Test on my phone
                  </button>
                )}
                {compulsory && (
                  <button className="btn btn-secondary btn-sm" onClick={() => standDown(t)} title="Stop it blocking sign-off">Stand down</button>
                )}
                {!isFocus && !compulsory && t.status === 'published' && (
                  <button className="btn btn-secondary btn-sm" onClick={() => setFocus(t.id)}><Radio size={13} /> Focus only</button>
                )}
                <button className="btn btn-secondary btn-sm" disabled={busyId === t.id} onClick={() => translate(t)}>
                  <Languages size={13} /> {busyId === t.id ? 'Translating…' : 'Translate'}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => remove(t)} title="Remove"><Trash2 size={13} /></button>
              </div>
            </div>
          );
        })}
      </div>

      {editing && <TopicEditor topic={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onSaved(); }} notify={notify} />}
      {previewing && <PreviewModal {...previewing} onClose={() => setPreviewing(null)} />}
      {genOpen && <GenerateModal onClose={() => setGenOpen(false)} onDrafted={(t) => { setGenOpen(false); setEditing(t); }} notify={notify} />}
    </div>
  );
}

const defaultSections = () => ([
  { key: 'critical', label: 'Critical Controls', items: [{ t: '', d: '' }] },
  { key: 'behaviours', label: 'Life Saving Behaviours', items: [{ t: '', d: '' }] },
  { key: 'hazards', label: 'Hazards', items: [{ t: '' }] },
  { key: 'response', label: 'If it happens', items: [{ t: '', d: '' }] },
]);

// ── Topic editor ──────────────────────────────────────────────────────────────
// Drop empty lines so a half-filled section never reaches a phone — and so the preview
// shows the same brief the save would write, not a draft peppered with blank bullets.
const cleanTopic = (t) => ({
  ...t,
  sections: (t.sections || [])
    .map(s => ({ ...s, items: (s.items || []).filter(it => (it.t || '').trim()) }))
    .filter(s => s.items.length),
});

function TopicEditor({ topic, onClose, onSaved, notify }) {
  const [t, setT] = useState({ ...topic, sections: topic.sections && topic.sections.length ? topic.sections : defaultSections() });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const setSection = (i, patch) => setT(x => ({ ...x, sections: x.sections.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));
  const setItem = (si, ii, patch) => setSection(si, { items: t.sections[si].items.map((it, j) => (j === ii ? { ...it, ...patch } : it)) });

  const save = async () => {
    setBusy(true);
    const r = await api('saveTopic', { topic: cleanTopic(t) });
    setBusy(false);
    if (r.success) { notify('Topic saved', 'success'); onSaved(); } else notify(r.error || 'Failed to save', 'error');
  };

  return (
    <Modal onClose={onClose} title={topic.id ? `Edit — ${topic.title}` : 'New safety topic'} wide>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: '0 0 70px' }}>
          <label style={labelStyle}>Emoji</label>
          <input className="input" style={inputStyle} value={t.emoji || ''} onChange={e => setT({ ...t, emoji: e.target.value })} />
        </div>
        <div style={{ flex: '1 1 200px' }}>
          <label style={labelStyle}>Title</label>
          <input className="input" style={inputStyle} value={t.title || ''} onChange={e => setT({ ...t, title: e.target.value })} />
        </div>
        <div style={{ flex: '2 1 300px' }}>
          <label style={labelStyle}>Subtitle — why it matters to a driver</label>
          <input className="input" style={inputStyle} value={t.subtitle || ''} onChange={e => setT({ ...t, subtitle: e.target.value })} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12, alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 160px' }}>
          <label style={labelStyle}>Category</label>
          <select className="input" style={inputStyle} value={t.category} onChange={e => setT({ ...t, category: e.target.value })}>
            <option>Critical Risk</option><option>High Risk</option>
          </select>
        </div>
        <div style={{ flex: '0 0 120px' }}>
          <label style={labelStyle}>Accent</label>
          <input className="input" type="color" style={inputStyle} value={t.accent || '#c9a84c'} onChange={e => setT({ ...t, accent: e.target.value })} />
        </div>
        <div style={{ flex: '1 1 200px' }}>
          <label style={labelStyle}>Illustrated guide URL (optional)</label>
          <input className="input" style={inputStyle} value={t.guideUrl || ''} placeholder="/dealing-with-dogs.html" onChange={e => setT({ ...t, guideUrl: e.target.value })} />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '.84rem', fontWeight: 700, cursor: 'pointer', paddingBottom: 8 }}>
          <input type="checkbox" checked={t.status === 'published'} onChange={e => setT({ ...t, status: e.target.checked ? 'published' : 'draft' })} /> Published
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '.84rem', fontWeight: 700, cursor: 'pointer', paddingBottom: 8 }}>
          <input type="checkbox" checked={t.showToDrivers !== false} onChange={e => setT({ ...t, showToDrivers: e.target.checked })} /> Show to drivers
        </label>
      </div>

      <ArtPicker ids={t.art || []} onChange={(art) => setT({ ...t, art })} notify={notify} />

      {(t.sections || []).map((s, si) => (
        <div key={si} className="card" style={{ padding: 12, marginTop: 14 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <input className="input" style={{ flex: '1 1 220px', fontWeight: 800 }} value={s.label} onChange={e => setSection(si, { label: e.target.value })} />
            <span style={{ fontSize: '.7rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)' }}>{s.key}</span>
            <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }}
              onClick={() => setSection(si, { items: [...s.items, { t: '', d: '' }] })}><Plus size={13} /></button>
          </div>
          {(s.items || []).map((it, ii) => (
            <div key={ii} style={{ display: 'flex', gap: 8, marginBottom: 7, alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <input className="input" style={inputStyle} placeholder="The line (max ~12 words)" value={it.t || ''}
                  onChange={e => setItem(si, ii, { t: e.target.value })} />
                {/* A textarea, not an input: a detail is sometimes a short list — the four
                    STAR steps, one per line — and the phone renders those lines as lines. */}
                <textarea className="input" rows={2} style={{ ...inputStyle, marginTop: 5, fontSize: '.82rem', resize: 'vertical', fontFamily: 'inherit' }}
                  placeholder="Optional detail — why or how. One line each if it is a list."
                  value={it.d || ''} onChange={e => setItem(si, ii, { d: e.target.value })} />
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => setSection(si, { items: s.items.filter((_, j) => j !== ii) })}><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      ))}

      {t.i18n && Object.keys(t.i18n).length > 0 && (
        <div style={{ fontSize: '.78rem', color: '#d97706', marginTop: 12 }}>
          Note: this topic already has {Object.keys(t.i18n).join(', ').toUpperCase()} translations. Editing the English
          does not re-translate — hit <strong>Translate</strong> on the card afterwards so the other languages match.
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <button className="btn btn-secondary" style={{ marginRight: 'auto' }}
          onClick={() => setPreview({ topic: cleanTopic(t), unsaved: true })}><Eye size={14} /> Preview</button>
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" disabled={busy} onClick={save}><Save size={14} /> {busy ? 'Saving…' : 'Save topic'}</button>
      </div>

      {preview && <PreviewModal {...preview} onClose={() => setPreview(null)} />}
    </Modal>
  );
}

// ── Illustrations ─────────────────────────────────────────────────────────────
// The pictures from the illustrated guides, ticked per topic. They ride at the top of
// the brief in the order chosen here, which is why order is editable and not just a set:
// the first picture is the one a driver sees before they read a word.
//
// The catalogue is ~100KB of SVG, so it is fetched once, when the picker is opened, and
// then held for the life of the page. The driver's phone never loads it — the backend
// inlines only the chosen pictures, in the driver's language.
const UPLOADS = 'Your uploads';
let artCache = null;
const loadArt = async () => {
  if (artCache) return artCache;
  const r = await api('artCatalogue');
  artCache = r.success ? r.art : [];
  return artCache;
};

const ArtThumb = ({ art, height = 78 }) => (
  art && art.url
    ? <img src={art.url} alt={art.caption || ''} style={{ display: 'block', width: '100%', height, objectFit: 'cover', background: '#fff' }} />
    : <div style={{ background: '#fff', borderRadius: 4, overflow: 'hidden', lineHeight: 0 }}
        dangerouslySetInnerHTML={{ __html: `<svg viewBox="0 0 400 260" xmlns="http://www.w3.org/2000/svg" style="display:block;width:100%;height:${height}px">${(art && art.svg) || ''}</svg>` }} />
);

function ArtPicker({ ids, onChange, notify }) {
  const [art, setArt] = useState(artCache);
  const [open, setOpen] = useState(false);
  useEffect(() => { if (!art) loadArt().then(setArt); }, [art]);

  // After an upload or a delete the catalogue is stale — refetch and hand it back so the
  // caller can select what it just added.
  const reload = useCallback(async () => {
    artCache = null;
    const fresh = await loadArt();
    setArt(fresh);
    return fresh;
  }, []);

  const byId = useMemo(() => Object.fromEntries((art || []).map(a => [a.id, a])), [art]);
  const move = (i, d) => {
    const next = [...ids];
    const j = i + d;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div className="card" style={{ padding: 12, marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <ImageIcon size={15} color="var(--hp-gold)" />
        <div style={{ fontWeight: 800, fontSize: '.85rem' }}>Illustrations</div>
        <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)', flex: 1, minWidth: 200 }}>
          {ids.length ? `${ids.length} picture${ids.length > 1 ? 's' : ''} — shown at the top of the brief, in this order.`
                      : 'None yet. A picture is read by the driver who skims the words.'}
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => setOpen(true)}>
          <ImageIcon size={13} /> {ids.length ? 'Change pictures' : 'Add pictures'}
        </button>
      </div>

      {ids.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          {ids.map((id, i) => {
            const a = byId[id];
            return (
              <div key={id} style={{ width: 150, border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                {a ? <ArtThumb art={a} /> : (
                  <div style={{ height: 78, display: 'grid', placeItems: 'center', fontSize: '.7rem', color: 'var(--hp-dim)' }}>
                    {art ? 'Missing picture' : 'Loading…'}
                  </div>
                )}
                <div style={{ padding: '5px 6px', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{ flex: 1, minWidth: 0, fontSize: '.66rem', fontWeight: 700, lineHeight: 1.3 }}>
                    {a ? a.caption : id}
                  </div>
                  <button className="btn btn-secondary btn-sm" style={{ padding: '2px 5px' }} disabled={i === 0}
                    onClick={() => move(i, -1)} title="Earlier"><ArrowLeft size={11} /></button>
                  <button className="btn btn-secondary btn-sm" style={{ padding: '2px 5px' }} disabled={i === ids.length - 1}
                    onClick={() => move(i, 1)} title="Later"><ArrowRight size={11} /></button>
                  <button className="btn btn-secondary btn-sm" style={{ padding: '2px 5px' }}
                    onClick={() => onChange(ids.filter(x => x !== id))} title="Remove"><X size={11} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && <ArtGallery art={art} ids={ids} onChange={onChange} onLibraryChange={reload}
        notify={notify} onClose={() => setOpen(false)} />}
    </div>
  );
}

function ArtGallery({ art, ids, onChange, onClose, onLibraryChange, notify }) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);

  // Anything the office already has — a photo of the actual corner, AP's own artwork.
  // Re-encoded in the browser first: a raw iPhone photo is EXIF-rotated and far too big
  // for the function's 6MB body once base64 adds its third.
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const buf = await uprightJpegBytes(file, 1400, 0.82);
      if (!buf) { notify('Could not read that image', 'error'); return; }
      const b64 = btoa(new Uint8Array(buf).reduce((acc, b) => acc + String.fromCharCode(b), ''));
      const caption = (window.prompt('Caption for this picture — it shows under it on the phone.', file.name.replace(/\.[^.]+$/, '')) || '').trim();
      const r = await api('uploadArt', { image: b64, caption });
      if (!r.success) { notify(r.error || 'Upload failed', 'error'); return; }
      const fresh = await onLibraryChange();
      if (fresh.some(a => a.id === r.id)) onChange([...ids, r.id]);
      notify('Picture added', 'success');
    } finally { setBusy(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete “${a.caption}”?\n\nAny brief using it simply loses that picture.`)) return;
    const r = await api('deleteArt', { id: a.id });
    if (!r.success) { notify(r.error || 'Could not delete', 'error'); return; }
    onChange(ids.filter(x => x !== a.id));
    await onLibraryChange();
    notify('Picture deleted', 'success');
  };
  const groups = useMemo(() => {
    const hit = (a) => !q.trim() || `${a.caption} ${a.tag} ${a.guide}`.toLowerCase().includes(q.trim().toLowerCase());
    const out = {};
    (art || []).filter(hit).forEach(a => { (out[a.guide] = out[a.guide] || []).push(a); });
    return out;
  }, [art, q]);

  const toggle = (id) => onChange(ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);

  return (
    <Modal title="Choose illustrations" onClose={onClose} wide zIndex={220}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <input className="input" style={{ flex: '1 1 220px' }} placeholder="Search — keys, dog, forklift, handbrake…"
          value={q} onChange={e => setQ(e.target.value)} />
        <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)' }}>
          {ids.length} chosen · tap a picture to add or remove it
        </div>
        <label className="btn btn-secondary btn-sm" style={{ cursor: busy ? 'wait' : 'pointer', margin: 0 }}>
          <Upload size={13} /> {busy ? 'Uploading…' : 'Upload a picture'}
          <input type="file" accept="image/*" disabled={busy} style={{ display: 'none' }}
            onChange={e => { const f = e.target.files && e.target.files[0]; e.target.value = ''; upload(f); }} />
        </label>
      </div>
      {!art && <div style={{ padding: 30, textAlign: 'center', color: 'var(--hp-dim)' }}><Loader size={18} /> Loading the library…</div>}
      {art && Object.keys(groups).sort((a, b) => (a === UPLOADS ? -1 : b === UPLOADS ? 1 : a.localeCompare(b))).map(guide => (
        <div key={guide} style={{ marginBottom: 18 }}>
          <div style={{ ...labelStyle, marginBottom: 7 }}>{guide}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(160px, 100%), 1fr))', gap: 10 }}>
            {groups[guide].map(a => {
              const on = ids.includes(a.id);
              return (
                <button key={a.id} onClick={() => toggle(a.id)}
                  style={{
                    padding: 0, textAlign: 'left', cursor: 'pointer', background: 'var(--hp-surface)',
                    border: `2px solid ${on ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
                    borderRadius: 'var(--radius)', overflow: 'hidden', fontFamily: 'var(--font-body)',
                  }}>
                  <ArtThumb art={a} height={92} />
                  <div style={{ padding: '6px 8px', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                    <input type="checkbox" readOnly checked={on} style={{ marginTop: 2, pointerEvents: 'none' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '.7rem', fontWeight: 800, lineHeight: 1.3, color: 'var(--hp-text)' }}>{a.caption}</div>
                      {a.tag && <div style={{ fontSize: '.64rem', color: 'var(--hp-dim)', marginTop: 1 }}>{a.tag}</div>}
                    </div>
                    {a.upload && (
                      <span role="button" tabIndex={0} title="Delete this picture"
                        onClick={e => { e.stopPropagation(); remove(a); }}
                        onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); remove(a); } }}
                        style={{ color: 'var(--hp-dim)', cursor: 'pointer', padding: 2 }}><Trash2 size={12} /></span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {art && !Object.keys(groups).length && (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--hp-dim)', fontSize: '.85rem' }}>Nothing matches “{q}”.</div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <button className="btn btn-primary" onClick={onClose}>Done</button>
      </div>
    </Modal>
  );
}

// ── Preview — the driver's screen, before anybody is made to read it ──────────
// Launch hands a brief to every phone and blocks the morning scan until it is read;
// that is a poor moment to find a typo or a line nobody translated. This renders the
// REAL portal component (`SafetyBriefGate`) against the REAL server projection, so
// what the office checks is what the phone shows — same scroll-to-unlock, same
// per-line English fallback. `preview` keeps it inert: no acknowledgement is written.
function PreviewModal({ topic, unsaved, onClose }) {
  const [lang, setLang] = useState('en');
  const [brief, setBrief] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let live = true;
    setBusy(true);
    api('previewTopic', unsaved ? { topic, lang } : { id: topic.id, lang }).then(r => {
      if (!live) return;
      if (r.success) { setBrief(r.topic); setErr(''); } else { setBrief(null); setErr(r.error || 'Could not build the preview'); }
      setBusy(false);
    });
    return () => { live = false; };
  }, [topic, unsaved, lang]);

  return (
    <Modal title={`Preview — ${topic.title || 'untitled brief'}`} onClose={onClose} zIndex={220}>
      <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)', marginBottom: 12, lineHeight: 1.5 }}>
        The brief as a driver's phone renders it{unsaved ? ', including the edits you have not saved yet' : ''}. Scroll it
        to the end the way they have to — the button only unlocks there. Nothing is recorded, and nobody is briefed until
        you press <strong style={{ color: 'var(--hp-text)' }}>Launch to drivers</strong>.
      </div>
      {brief && lang !== 'en' && !brief.translated && (
        <div style={{ fontSize: '.78rem', color: '#d97706', marginBottom: 10 }}>
          Not translated into this language yet — every line falls back to English. Use <strong>Translate</strong> on the
          topic card{unsaved ? ' once you have saved' : ''}, or the driver can translate it themselves.
        </div>
      )}
      <div style={{ maxWidth: 420, margin: '0 auto' }}>
        {busy && !brief && <div style={{ padding: 30, textAlign: 'center', color: 'var(--hp-dim)' }}><Loader size={18} /> Building the preview…</div>}
        {err && <div style={{ padding: 24, textAlign: 'center', color: '#ef4444', fontSize: '.85rem' }}>{err}</div>}
        {brief && <SafetyBriefGate key={lang} brief={brief} preview lang={lang} onLangChange={setLang} />}
      </div>
    </Modal>
  );
}

// ── Generate a topic from a document ──────────────────────────────────────────
// Text is extracted from the PDF in the browser (pdfjs, already bundled) and posted
// to Claude — same path the Retraining quiz generator uses.
async function pdfText(file) {
  const buf = await file.arrayBuffer();
  const { getDocument, GlobalWorkerOptions } = await import('pdfjs-dist');
  GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
  const doc = await getDocument({ data: buf }).promise;
  let out = '';
  const pages = Math.min(doc.numPages, 30);
  for (let p = 1; p <= pages; p++) {
    const page = await doc.getPage(p);
    const tc = await page.getTextContent();
    out += tc.items.map(i => i.str).join(' ') + '\n\n';
  }
  return out.trim();
}

function GenerateModal({ onClose, onDrafted, notify }) {
  const [file, setFile] = useState(null);
  const [topic, setTopic] = useState('');
  const [origin, setOrigin] = useState('Australia Post');
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState('');
  const [error, setError] = useState('');

  const run = async () => {
    if (!file) { setError('Choose a PDF first.'); return; }
    setError(''); setBusy(true); setPhase('reading');
    try {
      const text = await pdfText(file);
      if (!text || text.length < 40) { setError('No readable text in that PDF — it may be a scan. Type the points into a blank topic instead.'); return; }
      setPhase('drafting');
      const r = await api('generateTopic', { sourceText: text, topic: topic.trim(), filename: file.name, origin });
      if (!r.success) { setError(r.error || 'Could not draft the topic.'); return; }
      notify('Draft ready — check every line before you publish it', 'success');
      onDrafted(r.topic);
    } catch (e) {
      setError(e.message);
    } finally { setBusy(false); setPhase(''); }
  };

  return (
    <Modal onClose={onClose} title="New topic from a document">
      <div style={{ fontSize: '.84rem', color: 'var(--hp-dim)', lineHeight: 1.55, marginBottom: 14 }}>
        Drop an Australia Post critical-controls sheet, a SWMS or a manufacturer's safety page. The engine reads it and
        drafts a topic in the house shape — Critical Controls · Life Saving Behaviours · Hazards · If it happens.
        It comes back as a <strong>draft</strong>: read every line before you publish, and never let it invent a control
        the document did not say.
      </div>

      <label style={labelStyle}>PDF</label>
      <input className="input" style={inputStyle} type="file" accept="application/pdf" onChange={e => setFile(e.target.files[0] || null)} />

      <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 200px' }}>
          <label style={labelStyle}>What is it about? (optional hint)</label>
          <input className="input" style={inputStyle} value={topic} placeholder="e.g. Working at heights" onChange={e => setTopic(e.target.value)} />
        </div>
        <div style={{ flex: '1 1 160px' }}>
          <label style={labelStyle}>Source</label>
          <input className="input" style={inputStyle} value={origin} onChange={e => setOrigin(e.target.value)} />
        </div>
      </div>

      {error && <div style={{ color: '#ef4444', fontSize: '.83rem', marginTop: 12 }}>{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" disabled={busy} onClick={run}>
          {busy ? <><Loader size={14} /> {phase === 'reading' ? 'Reading PDF…' : 'Drafting…'}</> : <><Sparkles size={14} /> Draft the topic</>}
        </button>
      </div>
    </Modal>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Reports
// ══════════════════════════════════════════════════════════════════════════════
function ReportsTab({ data, onSaved, notify, scope }) {
  const [filter, setFilter] = useState('open');
  const [openId, setOpenId] = useState('');
  const [hazardFrom, setHazardFrom] = useState(null);
  const types = Object.fromEntries((data.reportTypes || []).map(t => [t.id, t]));

  const rows = (data.reports || []).filter(r => (filter === 'all' ? true : filter === 'open' ? r.status !== 'closed' : r.status === filter));

  const patch = async (id, fields) => {
    const r = await api('updateReport', { id, ...fields });
    if (r.success) { notify('Report updated', 'success'); onSaved(); } else notify(r.error || 'Failed', 'error');
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {[['open', 'Open'], ['new', 'New'], ['actioned', 'Actioned'], ['closed', 'Closed'], ['all', 'All']].map(([k, l]) => (
          <button key={k} className={`btn btn-sm ${filter === k ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter(k)}>{l}</button>
        ))}
      </div>

      {!rows.length && (
        <div className="card" style={{ padding: 30, textAlign: 'center', color: 'var(--hp-dim)' }}>
          No reports here. Drivers file these from the portal's Safety tab.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {rows.map(r => {
          const ty = types[r.type] || { label: r.type, emoji: '❗' };
          const isOpen = openId === r.id;
          return (
            <div key={r.id} className="card" style={{ padding: 14, borderLeft: `4px solid ${STATUS_COLOR[r.status] || 'var(--hp-dim)'}` }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '1.3rem' }}>{ty.emoji}</span>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 800 }}>{ty.label}</span>
                    <Chip text={r.status} color={STATUS_COLOR[r.status] || 'var(--hp-dim)'} />
                    {r.injured && <Chip text="Injury" color="#ef4444" />}
                    {r.hazard_id && <Chip text="On register" color="#16a34a" />}
                    {r.ap_notified && <Chip text="AP notified" color="#2563eb" />}
                  </div>
                  <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)', marginTop: 4 }}>
                    {r.driver_name || r.driver_login} · {fmtDate(r.occurred_on)}{r.occurred_at ? ` ${r.occurred_at}` : ''} · filed {fmtWhen(r.created_at)}
                    {r.lang && r.lang !== 'en' ? ` · reported in ${r.lang.toUpperCase()}` : ''}
                  </div>
                  {r.address && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 6, fontWeight: 700, fontSize: '.87rem' }}>
                      <MapPin size={13} color="var(--hp-gold)" /> {r.address}
                    </div>
                  )}
                  <div style={{ fontSize: '.86rem', marginTop: 6, lineHeight: 1.5 }}>{r.description}</div>
                </div>
                <button className="btn btn-secondary btn-sm" onClick={() => setOpenId(isOpen ? '' : r.id)}>{isOpen ? 'Close' : 'Open'}</button>
              </div>

              {isOpen && (
                <div style={{ marginTop: 14, borderTop: '1px solid var(--hp-border)', paddingTop: 14 }}>
                  {r.action_taken && <Field label="What the driver did at the time" value={r.action_taken} />}
                  {r.injured && <Field label="Injury" value={r.injury_detail || 'Reported, no detail given'} />}
                  {(r.witnesses || []).length > 0 && (
                    <Field label="Witnesses" value={r.witnesses.map(w => `${w.name}${w.phone ? ` (${w.phone})` : ''}`).join(' · ')} />
                  )}
                  {(r.latitude || r.longitude) && (
                    <Field label="Location" value={
                      <a href={`https://maps.google.com/?q=${r.latitude},${r.longitude}`} target="_blank" rel="noreferrer" style={{ color: 'var(--hp-gold)' }}>
                        {r.latitude}, {r.longitude}
                      </a>
                    } />
                  )}

                  {(r.photos || []).length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <label style={labelStyle}>Photos</label>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {r.photos.map((p, i) => (
                          <a key={i} href={p.url} target="_blank" rel="noreferrer">
                            <img src={p.url} alt={p.label} style={{ width: 130, height: 130, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--hp-border)' }} />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {(r.admin_notes || []).length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <label style={labelStyle}>Office notes</label>
                      {r.admin_notes.map((n, i) => (
                        <div key={i} style={{ fontSize: '.82rem', marginBottom: 4 }}>
                          <span style={{ color: 'var(--hp-dim)' }}>{fmtWhen(n.at)} · {n.by}</span> — {n.body}
                        </div>
                      ))}
                    </div>
                  )}

                  <Triage report={r} onPatch={patch} onHazard={() => setHazardFrom(r)} scope={scope} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {hazardFrom && (
        <HazardEditor
          actions={data.hazardActions}
          scope={scope}
          hazard={{
            address: hazardFrom.address,
            street: hazardFrom.street || '',
            suburb: hazardFrom.suburb || '',
            scope: hazardFrom.address ? 'address' : 'general',
            kind: hazardFrom.type,
            action: (types[hazardFrom.type] || {}).defaultAction || 'caution',
            from_report: hazardFrom.id,
            driver_note: '',
          }}
          onClose={() => setHazardFrom(null)}
          onSaved={() => { setHazardFrom(null); onSaved(); }}
          notify={notify}
        />
      )}
    </div>
  );
}

const Field = ({ label, value }) => (
  <div style={{ marginTop: 10 }}>
    <label style={labelStyle}>{label}</label>
    <div style={{ fontSize: '.86rem', lineHeight: 1.5 }}>{value}</div>
  </div>
);

function Triage({ report, onPatch, onHazard, scope }) {
  const [note, setNote] = useState('');
  const [outcome, setOutcome] = useState(report.outcome || '');
  const [apRef, setApRef] = useState(report.ap_reference || '');
  // Notifying Australia Post is an office act — a leader investigates and records the
  // outcome, the office sends the notification. The backend enforces this too.
  const isAdmin = !scope || scope.isAdmin;

  return (
    <div style={{ marginTop: 14, borderTop: '1px dashed var(--hp-border)', paddingTop: 12 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 240px' }}>
          <label style={labelStyle}>Outcome — the driver sees this on their report</label>
          <input className="input" style={inputStyle} value={outcome} onChange={e => setOutcome(e.target.value)}
            placeholder="e.g. Address on the register as DO NOT ENTER; AP asked to stop delivery" />
        </div>
        {isAdmin && (
          <div style={{ flex: '1 1 200px' }}>
            <label style={labelStyle}>AP reference</label>
            <input className="input" style={inputStyle} value={apRef} onChange={e => setApRef(e.target.value)} placeholder="who / when / case no." />
          </div>
        )}
      </div>
      <div style={{ marginTop: 10 }}>
        <label style={labelStyle}>Office note (internal)</label>
        <input className="input" style={inputStyle} value={note} onChange={e => setNote(e.target.value)} placeholder="What you did, who you spoke to" />
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
        {['reviewing', 'actioned', 'closed'].map(s => (
          <button key={s} className="btn btn-secondary btn-sm"
            onClick={() => onPatch(report.id, { status: s, outcome, ap_reference: apRef, note })}>
            Mark {s}
          </button>
        ))}
        {isAdmin && (
          <button className="btn btn-secondary btn-sm" onClick={() => onPatch(report.id, { ap_notified: !report.ap_notified, outcome, ap_reference: apRef, note })}>
            {report.ap_notified ? 'Un-flag AP notified' : 'Flag AP notified'}
          </button>
        )}
        {report.address && !report.hazard_id && (
          <button className="btn btn-primary btn-sm" onClick={onHazard}>
            <AlertTriangle size={13} /> Put this address on the register
          </button>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Hazard register
// ══════════════════════════════════════════════════════════════════════════════
// The register holds two different kinds of decision and they deserve to be read apart.
// A PRE-CARD DELIVERY POINT is an address we have decided is not safe to deliver to the
// door at all — the driver cards it on the spot and the parcel goes to the Post Office.
// Everything else on the register is a hazard the driver still delivers through. Same
// list, same routing to the driver, same acknowledgement; it is the standing action that
// separates them. Paul, 28 Aug 2026.
const PRECARD_ACTIONS = ['do_not_enter', 'card_to_po'];
const isPrecard = (h) => PRECARD_ACTIONS.includes(h.action) && h.scope !== 'general' && h.action !== 'cleared';

function HazardsTab({ data, onSaved, notify, scope }) {
  const [editing, setEditing] = useState(null);
  const [showCleared, setShowCleared] = useState(false);
  const rows = (data.hazards || []).filter(h => showCleared || (h.active !== false && h.action !== 'cleared'));
  const precard = rows.filter(h => isPrecard(h) && h.active !== false);
  const others = rows.filter(h => !(isPrecard(h) && h.active !== false));
  const actionOf = (id) => (data.hazardActions || []).find(a => a.id === id) || {};

  const remove = async (h) => {
    if (!window.confirm(`Remove ${h.address} from the hazard register? Drivers will stop seeing the warning.`)) return;
    const r = await api('deleteHazard', { id: h.id });
    if (r.success) { notify('Removed from the register', 'success'); onSaved(); } else notify(r.error || 'Failed', 'error');
  };

  return (
    <div>
      <div className="card" style={{ padding: 14, marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <AlertTriangle size={16} color="#d97706" />
        <div style={{ flex: 1, minWidth: 240, fontSize: '.83rem', color: 'var(--hp-dim)' }}>
          An <strong style={{ color: 'var(--hp-text)' }}>address</strong> hazard is forced in front of the driver whose route covers
          that street — they cannot get past it without tapping "I have read this", and the tap is stamped against them. A
          <strong style={{ color: 'var(--hp-text)' }}> general</strong> hazard is forced on everyone. Both are listed for every
          driver to look up. This is the Unsafe Delivery Points register the WHS plan promised.
          {data.routesLoaded === false && (
            <div style={{ color: '#ef4444', fontWeight: 700, marginTop: 6 }}>
              The route register could not be read, so address hazards are going to EVERY driver until it is back.
            </div>
          )}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.8rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={showCleared} onChange={e => setShowCleared(e.target.checked)} /> Show cleared
        </label>
        <button className="btn btn-secondary btn-sm" onClick={() => setEditing({ address: '', scope: 'general', kind: 'hazard', action: 'caution', driver_note: '' })}>
          <Plus size={14} /> General hazard
        </button>
        <button className="btn btn-primary btn-sm" onClick={() => setEditing({ address: '', scope: 'address', kind: 'unsafe_point', action: 'caution', driver_note: '' })}>
          <Plus size={14} /> Add an address
        </button>
      </div>

      {!rows.length && <div className="card" style={{ padding: 30, textAlign: 'center', color: 'var(--hp-dim)' }}>Nothing on the register.</div>}

      {/* Pre-Card points have their own tab — they carry an Australia Post notification
          and a date advised, which is a different job from warning a driver. */}
      {!!precard.length && (
        <div className="card" style={{ padding: 12, marginBottom: 14, borderLeft: '4px solid #dc2626', fontSize: '.82rem' }}>
          <strong style={{ color: '#dc2626' }}>{precard.length} Pre-Card delivery point{precard.length > 1 ? 's' : ''}</strong>
          {' '}live — addresses nobody walks up to. They are managed on the <strong>Pre-Card</strong> tab,
          where Australia Post is notified and the date advised is recorded.
        </div>
      )}
      <HazardList rows={others} actionOf={actionOf} setEditing={setEditing} remove={remove} scope={scope} />
      {!others.length && !!precard.length && (
        <div className="card" style={{ padding: 26, textAlign: 'center', color: 'var(--hp-dim)' }}>
          No other hazards — everything live is a Pre-Card point.
        </div>
      )}

      {editing && (
        <HazardEditor actions={data.hazardActions} hazard={editing} scope={scope}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onSaved(); }} notify={notify} />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Pre-Card delivery points — the tab
// ══════════════════════════════════════════════════════════════════════════════
// THIS IS WHERE THE PRE-CARD REGISTER LIVES: /pre-card, which is this tab of the Safety
// page. It is the hazard register filtered to the entries whose standing action is "do
// not attempt" — one register, not two, so a dangerous address cannot be recorded in one
// place and missed in the other.
//
// It gets its own tab because a Pre-Card point carries an obligation the rest of the
// register does not: Australia Post is changing how that address is served, so they have
// to be told, and we have to be able to prove the date we told them. That date is written
// by the send — never typed. Paul, 31 Aug 2026.
function PrecardTab({ data, onSaved, notify, scope }) {
  const [editing, setEditing] = useState(null);
  const [notifying, setNotifying] = useState(null);
  const rows = (data.hazards || []).filter(h => isPrecard(h) && h.active !== false);
  const actionOf = (id) => (data.hazardActions || []).find(a => a.id === id) || {};
  const unadvised = rows.filter(h => !h.ap_notified).length;

  const remove = async (h) => {
    if (!window.confirm(`Remove ${h.address} from the Pre-Card register? Drivers will stop seeing the warning and it goes back to normal delivery.`)) return;
    const r = await api('deleteHazard', { id: h.id });
    if (r.success) { notify('Removed from the register', 'success'); onSaved(); } else notify(r.error || 'Failed', 'error');
  };

  return (
    <div>
      <div className="card" style={{ padding: 14, marginBottom: 16, borderLeft: '4px solid #dc2626' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <ShieldAlert size={17} color="#dc2626" style={{ marginTop: 2 }} />
          <div style={{ flex: 1, minWidth: 260, fontSize: '.83rem', color: 'var(--hp-dim)', lineHeight: 1.55 }}>
            <strong style={{ color: 'var(--hp-text)' }}>Pre-Card</strong> means the driver does not deliver to the door:
            they issue a card at the point of delivery and the article goes to the Post Office for the customer to collect.
            These addresses are part of the hazard register, so each one reaches the driver whose route covers the street
            and has to be acknowledged. A customer case that arrives for one of them is flagged on Customer Cases before
            anyone answers it.
            {unadvised > 0 && (
              <div style={{ color: '#dc2626', fontWeight: 700, marginTop: 7 }}>
                {unadvised} point{unadvised > 1 ? 's have' : ' has'} not been advised to Australia Post yet.
              </div>
            )}
          </div>
          <button className="btn btn-primary btn-sm"
            onClick={() => setEditing({ address: '', scope: 'address', kind: 'unsafe_point', action: 'card_to_po', notify: 'route', driver_note: '' })}>
            <Plus size={14} /> Add a Pre-Card point
          </button>
        </div>
      </div>

      {!rows.length && (
        <div className="card" style={{ padding: 30, textAlign: 'center', color: 'var(--hp-dim)' }}>
          No Pre-Card delivery points. Add one here, or from the case that revealed it on Customer Cases.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map(h => {
          const a = actionOf(h.action);
          return (
            <div key={h.id} className="card" style={{ padding: 14, borderLeft: '4px solid #dc2626' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <MapPin size={16} color="#dc2626" style={{ marginTop: 2 }} />
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontWeight: 800, fontSize: '.93rem' }}>{h.address}</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                    <Chip text={a.label || h.action} color="#dc2626" />
                    <Chip text={h.notify === 'all' ? 'All drivers' : 'Route driver'} color={h.notify === 'all' ? '#2563eb' : 'var(--hp-gold)'} />
                    <Chip text={h.kind} color="var(--hp-dim)" />
                    {/* The date we told AP is the point of this tab, so it is a chip, not a footnote. */}
                    {h.ap_notified
                      ? <Chip text={`AP advised ${h.ap_notified_at ? fmtDate(h.ap_notified_at) : ''}`.trim()} color="#16a34a" />
                      : <Chip text="AP NOT ADVISED" color="#dc2626" />}
                  </div>
                  <ReachLine hazard={h} />
                  <CaseHistoryLine hazard={h} />
                  {h.driver_note && <div style={{ fontSize: '.83rem', marginTop: 7, lineHeight: 1.5, whiteSpace: 'pre-line' }}>{h.driver_note}</div>}
                  {h.office_note && <div style={{ fontSize: '.79rem', color: 'var(--hp-dim)', marginTop: 5, lineHeight: 1.5 }}>Office: {h.office_note}</div>}
                  <ApAdviceTrail hazard={h} />
                  <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginTop: 7 }}>
                    Added {fmtWhen(h.created_at)}{h.created_by ? ` by ${h.created_by}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button className={`btn btn-sm ${h.ap_notified ? 'btn-secondary' : 'btn-primary'}`} onClick={() => setNotifying(h)}>
                    <Send size={13} /> {h.ap_notified ? 'Advise AP again' : 'Advise Australia Post'}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditing(h)}>Edit</button>
                  {(!scope || scope.isAdmin) && (
                    <button className="btn btn-secondary btn-sm" onClick={() => remove(h)}><Trash2 size={13} /></button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <HazardEditor actions={data.hazardActions} hazard={editing} scope={scope}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onSaved(); }} notify={notify} />
      )}
      {notifying && (
        <NotifyApModal hazard={notifying} onClose={() => setNotifying(null)}
          onSent={() => { setNotifying(null); onSaved(); }} notify={notify} />
      )}
    </div>
  );
}

// Every advice we have sent about this address, oldest first. Kept as a list rather than
// one date because a re-advice to a new AP contact must not erase the first one — the
// first is the date that counts if the address is ever disputed.
function ApAdviceTrail({ hazard }) {
  const sent = hazard.ap_emails || [];
  if (!sent.length) return null;
  return (
    <div style={{ fontSize: '.75rem', color: 'var(--hp-dim)', marginTop: 7, lineHeight: 1.6 }}>
      {sent.map((e, i) => (
        <div key={i}>
          <CheckCircle size={11} style={{ verticalAlign: '-1px', color: '#16a34a', marginRight: 4 }} />
          Advised {fmtWhen(e.at)} — {(e.to || []).join(', ')}{e.by ? ` · sent by ${e.by}` : ''}
        </div>
      ))}
    </div>
  );
}

// The letter, shown before it goes. The body is built server-side and editable here: what
// the office reads is what AP receives is what the register records.
function NotifyApModal({ hazard, onClose, onSent, notify }) {
  const [draft, setDraft] = useState(null);
  const [to, setTo] = useState([]);
  const [oneOff, setOneOff] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      const r = await api('draftApPrecard', { id: hazard.id });
      if (!alive) return;
      if (r.success) {
        setDraft({ subject: r.subject, body: r.body, recipients: r.recipients || [] });
        // Default to the Australia Post addresses in the book; our own copies are bcc'd
        // anyway, so pre-ticking them would just send us duplicates.
        setTo((r.recipients || []).filter(x => /auspost\.com\.au$/i.test(x.email || '')).map(x => x.email));
      } else notify(r.error || 'Could not build the letter', 'error');
    })();
    return () => { alive = false; };
  }, [hazard.id, notify]);

  const toggle = (email) => setTo(list => (list.includes(email) ? list.filter(e => e !== email) : [...list, email]));

  const send = async () => {
    const all = [...to, ...oneOff.split(/[,;]/).map(s => s.trim()).filter(Boolean)];
    if (!all.length) { notify('Choose at least one recipient', 'error'); return; }
    setBusy(true);
    const r = await api('notifyApPrecard', { id: hazard.id, to: all, subject: draft.subject, body: draft.body });
    setBusy(false);
    if (r.success) {
      notify(`Australia Post advised — ${all.length} recipient${all.length > 1 ? 's' : ''}, dated ${fmtDate(r.at)}`, 'success');
      onSent();
    } else notify(r.error || 'Send failed', 'error');
  };

  return (
    <Modal onClose={onClose} title="Advise Australia Post of a Pre-Card delivery point">
      {!draft ? <div style={{ padding: 20, color: 'var(--hp-dim)' }}><Loader size={15} /> Building the letter…</div> : (
        <>
          <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)', lineHeight: 1.55, marginBottom: 12 }}>
            This tells Australia Post that <strong style={{ color: 'var(--hp-text)' }}>{hazard.address}</strong> is now
            pre-carded, and why. The date it sends is recorded against the address on the register — that is the date
            we would rely on if the address is ever queried.
            {hazard.ap_notified && (
              <div style={{ color: '#d97706', fontWeight: 700, marginTop: 6 }}>
                Already advised {hazard.ap_notified_at ? fmtDate(hazard.ap_notified_at) : 'previously'}. Sending again adds
                a second advice — the original date is kept.
              </div>
            )}
          </div>

          <label style={labelStyle}>Send to</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {(draft.recipients || []).map(rec => (
              <button key={rec.email} className={`btn btn-sm ${to.includes(rec.email) ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => toggle(rec.email)}>{rec.name || rec.email}</button>
            ))}
          </div>
          {!draft.recipients.length && (
            <div style={{ fontSize: '.78rem', color: '#d97706', marginTop: 6 }}>
              No saved recipients — add addresses below. The address book is shared with the Issue Register.
            </div>
          )}
          <input className="input" style={{ ...inputStyle, marginTop: 8 }} value={oneOff} onChange={e => setOneOff(e.target.value)}
            placeholder="Or type an address — comma-separated for more than one" />
          <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)', marginTop: 5 }}>A copy is always bcc'd to paul@hollypaul.com.</div>

          <label style={{ ...labelStyle, marginTop: 12 }}>Subject</label>
          <input className="input" style={inputStyle} value={draft.subject} onChange={e => setDraft({ ...draft, subject: e.target.value })} />

          <label style={{ ...labelStyle, marginTop: 12 }}>The letter — read it before it goes</label>
          <textarea className="input" style={{ ...inputStyle, minHeight: 300, fontFamily: 'var(--font-mono)', fontSize: '.78rem', lineHeight: 1.6 }}
            value={draft.body} onChange={e => setDraft({ ...draft, body: e.target.value })} />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" disabled={busy} onClick={send}><Send size={14} /> {busy ? 'Sending…' : 'Send to Australia Post'}</button>
          </div>
        </>
      )}
    </Modal>
  );
}

function SectionHead({ title, blurb, color }) {
  return (
    <div style={{ margin: '18px 0 10px' }}>
      <div style={{ fontWeight: 800, fontSize: '.86rem', color, display: 'flex', alignItems: 'center', gap: 7 }}>
        <AlertTriangle size={14} /> {title}
      </div>
      <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)', marginTop: 3, lineHeight: 1.5, maxWidth: 760 }}>{blurb}</div>
    </div>
  );
}

function HazardList({ rows, actionOf, setEditing, remove, scope }) {
  if (!rows.length) return null;
  return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map(h => {
          const a = actionOf(h.action);
          const col = TONE_COLOR[a.tone] || '#d97706';
          return (
            <div key={h.id} className="card" style={{ padding: 14, borderLeft: `4px solid ${col}` }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <MapPin size={16} color={col} style={{ marginTop: 2 }} />
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontWeight: 800, fontSize: '.93rem' }}>{h.address}</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                    <Chip text={a.label || h.action} color={col} />
                    <Chip text={h.scope === 'general' ? 'All drivers' : 'Address'} color={h.scope === 'general' ? '#2563eb' : 'var(--hp-gold)'} />
                    <Chip text={h.kind} color="var(--hp-dim)" />
                    {h.ap_notified && <Chip text="AP notified" color="#2563eb" />}
                    {h.review_on && <Chip text={`Review ${fmtDate(h.review_on)}`} color="var(--hp-gold)" />}
                  </div>
                  <ReachLine hazard={h} />
                  <CaseHistoryLine hazard={h} />
                  {h.driver_note && <div style={{ fontSize: '.83rem', marginTop: 7, lineHeight: 1.5, whiteSpace: 'pre-line' }}>{h.driver_note}</div>}
                  {h.office_note && <div style={{ fontSize: '.79rem', color: 'var(--hp-dim)', marginTop: 5, lineHeight: 1.5 }}>Office: {h.office_note}</div>}
                  <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginTop: 7 }}>
                    Added {fmtWhen(h.created_at)}{h.created_by ? ` by ${h.created_by}` : ''}{h.from_report ? ' · from a driver report' : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditing(h)}>Edit</button>
                  {(!scope || scope.isAdmin) && (
                    <button className="btn btn-secondary btn-sm" onClick={() => remove(h)}><Trash2 size={13} /></button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
  );
}

// The Australia Post cases we have had at this address. Matched live on the address
// rather than stored on the case, so a point added today immediately shows the cases
// that prompted it. This is the log: the evidence that an address belongs on the
// register, instead of it living in somebody's memory.
function CaseHistoryLine({ hazard }) {
  const h = hazard.cases;
  if (!h || !h.count) return null;
  const recent = (h.cases || []).slice(0, 4);
  return (
    <div style={{ fontSize: '.78rem', color: '#dc2626', marginTop: 6, fontWeight: 700 }}>
      {h.count} customer case{h.count > 1 ? 's' : ''} at this address
      {!!recent.length && (
        <span style={{ fontWeight: 400, color: 'var(--hp-dim)' }}>
          {' — '}
          {recent.map(c => (
            <Link key={c.id} to={`/customer-cases?case=${encodeURIComponent(c.case_number || '')}`}
              style={{ color: 'var(--hp-gold)', marginRight: 8 }}
              title={`${c.category || 'case'}${c.driver_login ? ` · ${c.driver_login}` : ''} · ${c.status}`}>
              {c.case_number || 'case'}
            </Link>
          ))}
          {h.count > recent.length && <span>+{h.count - recent.length} more</span>}
        </span>
      )}
    </div>
  );
}

// Who a hazard actually lands on, resolved live against the route register. The point
// of showing it here is that a typo in the street name is otherwise invisible: it looks
// saved, and reaches nobody.
function ReachLine({ hazard }) {
  const r = hazard.resolved;
  if (hazard.scope === 'general') {
    return <div style={{ fontSize: '.78rem', color: '#2563eb', marginTop: 6, fontWeight: 700 }}>Forced on every driver.</div>;
  }
  if (!r) return null;
  // Widened on purpose — say so, so it never looks like a routing accident.
  if (hazard.notify === 'all') {
    return (
      <div style={{ fontSize: '.78rem', color: '#2563eb', marginTop: 6, fontWeight: 700 }}>
        Pushed to EVERY driver by choice{r.logins.length ? ` (route: ${r.logins.join(', ')})` : ''}.
      </div>
    );
  }
  // The one state that needs fixing. This used to be forced on every driver instead;
  // now it interrupts nobody, so the register has to be the thing that says so.
  if (r.match === 'unmatched') {
    return (
      <div style={{ fontSize: '.78rem', color: '#dc2626', marginTop: 6, fontWeight: 700, lineHeight: 1.5 }}>
        ⚠ Reaches nobody — no route covers “{r.canon || hazard.street || hazard.address}”.
        <span style={{ fontWeight: 400, color: 'var(--hp-dim)' }}> It is listed under All hazards but stops no one at the gate.
          Fix the street spelling, or switch it to “Every driver”.</span>
      </div>
    );
  }
  const label = r.match === 'shared'
    ? `Shared street — forced on all ${r.logins.length}: ${r.logins.join(', ')}`
    : `Forced on ${r.logins.join(', ')}${r.via ? ` (route: ${r.via})` : ''}`;
  return <div style={{ fontSize: '.78rem', color: '#16a34a', marginTop: 6, fontWeight: 700 }}>{label}</div>;
}

function HazardEditor({ hazard, actions, onClose, onSaved, notify, scope }) {
  const [h, setH] = useState(hazard);
  const [busy, setBusy] = useState(false);
  const chosen = (actions || []).find(a => a.id === h.action);
  const isAdmin = !scope || scope.isAdmin;
  const isGeneral = h.scope === 'general';

  const save = async () => {
    setBusy(true);
    const r = await api('saveHazard', { hazard: h });
    setBusy(false);
    if (r.success) { notify('Saved to the hazard register — drivers see it now', 'success'); onSaved(); }
    else notify(r.error || 'Failed to save', 'error');
  };

  return (
    <Modal onClose={onClose} title={h.id ? 'Edit hazard' : 'Add to the hazard register'}>
      <label style={labelStyle}>Who does this reach?</label>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {[['address', 'A specific address', 'Forced on the driver whose route covers that street'],
          ['general', 'All drivers', 'Forced on everyone — no address needed']].map(([k, label, hint]) => (
          <button key={k} className={`btn btn-sm ${(h.scope || 'address') === k ? 'btn-primary' : 'btn-secondary'}`}
            title={hint} onClick={() => setH({ ...h, scope: k })}>{label}</button>
        ))}
      </div>

      {!isGeneral && (
        <>
          <label style={{ ...labelStyle, marginTop: 12 }}>Address</label>
          <input className="input" style={inputStyle} value={h.address || ''} placeholder="42 Victoria Street, Potts Point 2011"
            onChange={e => setH({ ...h, address: e.target.value })} />

          {/* WHO it reaches is now the office's decision rather than a side effect of
              whether the street happened to match a route. An address hazard that lands
              on every driver's home screen trains all of them to swipe past the gate. */}
          <label style={{ ...labelStyle, marginTop: 12 }}>Push it to</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[['route', 'Just the route driver', 'Only the driver whose route lists this street. Everyone else can look it up under All hazards.'],
              ['all', 'Every driver', 'Forced on every driver, the same as a general hazard. Use it when anyone could be sent there.']].map(([k, lbl, hint]) => (
              <button key={k} title={hint} className={`btn btn-sm ${(h.notify || 'route') === k ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setH({ ...h, notify: k })}>{lbl}</button>
            ))}
          </div>
          <div style={{ fontSize: '.76rem', color: 'var(--hp-dim)', marginTop: 6, lineHeight: 1.5 }}>
            {(h.notify || 'route') === 'all'
              ? 'Every driver has to read this before they go, whether the address is on their round or not.'
              : 'Only the driver whose route covers this street is stopped by it. Every other driver still sees it in their Safety page under All hazards — it just does not interrupt them.'}
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
        {!isGeneral && (
          <>
            <div style={{ flex: '1 1 150px' }}>
              <label style={labelStyle}>Street — overrides matching</label>
              <input className="input" style={inputStyle} value={h.street || ''} placeholder="Victoria Street"
                onChange={e => setH({ ...h, street: e.target.value })} />
            </div>
            <div style={{ flex: '1 1 120px' }}>
              <label style={labelStyle}>Suburb</label>
              <input className="input" style={inputStyle} value={h.suburb || ''} onChange={e => setH({ ...h, suburb: e.target.value })} />
            </div>
          </>
        )}
        <div style={{ flex: '1 1 130px' }}>
          <label style={labelStyle}>Kind</label>
          <select className="input" style={inputStyle} value={h.kind || 'unsafe_point'} onChange={e => setH({ ...h, kind: e.target.value })}>
            <option value="dog">Dog</option>
            <option value="unsafe_point">Unsafe delivery point</option>
            <option value="abuse">Abuse / threat</option>
            <option value="hazard">Other hazard</option>
          </select>
        </div>
      </div>
      {!isGeneral && (
        <div style={{ fontSize: '.76rem', color: 'var(--hp-dim)', marginTop: 6 }}>
          The street is how this finds the right driver. Leave it blank and we read it out of the address; fill it in if the
          address is written oddly and the match comes back wrong.
        </div>
      )}

      <label style={{ ...labelStyle, marginTop: 12 }}>Standing action — what every driver must do</label>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {(actions || []).map(a => (
          <button key={a.id} className={`btn btn-sm ${h.action === a.id ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setH({ ...h, action: a.id })}>{a.label}</button>
        ))}
      </div>
      {chosen && (
        <div style={{ marginTop: 8, fontSize: '.82rem', color: 'var(--hp-dim)', borderLeft: `3px solid ${TONE_COLOR[chosen.tone]}`, paddingLeft: 10 }}>
          Driver sees: “{chosen.driver}”
        </div>
      )}
      {h.id && h.notified_at && (
        <div style={{ marginTop: 8, fontSize: '.78rem', color: '#d97706' }}>
          Changing the action or the driver note re-opens this for everyone it reaches — they will have to read it again.
        </div>
      )}

      <label style={{ ...labelStyle, marginTop: 12 }}>
        {isGeneral ? 'What drivers must do (required)' : 'Why — the driver reads this, and it is the reason quoted to Australia Post'}
      </label>
      {/* A textarea, not an input: a reason is usually several facts, and one fact per line
          is how it reads on the driver's card AND in the letter to Australia Post. A
          single-line input silently flattened them the moment the entry was reopened. */}
      <textarea className="input" style={{ ...inputStyle, minHeight: 84, lineHeight: 1.5, resize: 'vertical' }} value={h.driver_note || ''}
        placeholder={isGeneral ? 'e.g. Loading dock ramp is being resurfaced — use the George Street entrance all week' : 'One fact per line, e.g.\nThe building is locked.\nThe intercom does not work.'}
        onChange={e => setH({ ...h, driver_note: e.target.value })} />
      <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)', marginTop: 4 }}>
        One fact per line. These are the reasons — the instruction is already on the standing action above.
      </div>

      <label style={{ ...labelStyle, marginTop: 12 }}>Office note (internal)</label>
      <input className="input" style={inputStyle} value={h.office_note || ''} onChange={e => setH({ ...h, office_note: e.target.value })} />

      <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '0 0 160px' }}>
          <label style={labelStyle}>Review on</label>
          <input className="input" type="date" style={inputStyle} value={h.review_on || ''} onChange={e => setH({ ...h, review_on: e.target.value })} />
        </div>
        {isAdmin && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '.84rem', fontWeight: 700, cursor: 'pointer', paddingBottom: 8 }}>
            <input type="checkbox" checked={!!h.ap_notified} onChange={e => setH({ ...h, ap_notified: e.target.checked })} /> Australia Post notified
          </label>
        )}
        {isAdmin && h.ap_notified && (
          <div style={{ flex: '1 1 220px' }}>
            <label style={labelStyle}>AP reference</label>
            <input className="input" style={inputStyle} value={h.ap_reference || ''} onChange={e => setH({ ...h, ap_reference: e.target.value })} />
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" disabled={busy} onClick={save}><Save size={14} /> {busy ? 'Saving…' : 'Save to register'}</button>
      </div>
    </Modal>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Awareness — the evidence trail
// ══════════════════════════════════════════════════════════════════════════════
function AwarenessTab({ acks, hazardAcks, people, topics, langs, notify }) {
  const [busy, setBusy] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [view, setView] = useState('grid');
  const [topicId, setTopicId] = useState('');

  const logins = [...new Set((acks || []).map(a => a.login))].sort();
  const published = (topics || []).filter(t => t.status === 'published' && t.showToDrivers !== false);
  // One topic selected narrows the whole tab to it — the grid to that column, the list
  // and the download to those rows.
  const shown = topicId ? published.filter(t => t.id === topicId) : published;
  const key = (l, t) => `${l}|${t}`;
  const langName = (c) => ((langs || []).find(l => l.code === c) || {}).flag || c;
  // The grid is a flag per cell; a downloaded register has to spell the language out.
  const langLabel = (c) => ((langs || []).find(l => l.code === c) || {}).label || c || '';
  const who = (login) => (people || {})[(login || '').toUpperCase()] || {};
  const titleOf = (a) => a.title || ((topics || []).find(t => t.id === a.topicId) || {}).title || a.topicId;
  const emojiOf = (a) => ((topics || []).find(t => t.id === a.topicId) || {}).emoji || '';

  // The download range. An acknowledgement carries only FIRST and LAST — every read in
  // between is collapsed into the count — so "read in this period" is an overlap test,
  // not an equality one: include it if the reading spans any part of the range. Both
  // ends are inclusive whole days, read in local time (the stamps are UTC).
  const inRange = (a) => {
    if (!from && !to) return true;
    const first = a.first || a.last;
    const last = a.last || a.first;
    if (!first && !last) return false;
    if (from && new Date(last) < new Date(`${from}T00:00:00`)) return false;
    if (to && new Date(first) > new Date(`${to}T23:59:59.999`)) return false;
    return true;
  };
  const inRangeAcks = (acks || []).filter(inRange).filter(a => !topicId || a.topicId === topicId);
  // Address hazards are not a topic, so a topic filter puts them out of scope entirely
  // rather than showing an empty table that reads like "nobody acknowledged one".
  const inRangeHazards = topicId ? null : (hazardAcks || []).filter(inRange);
  // The range is a lens on the whole tab, not just the file: the grid ticks, the list
  // and the download all say the same thing. The driver roster is deliberately NOT
  // filtered — a driver who read nothing in the window is the point of asking.
  const map = Object.fromEntries(inRangeAcks.map(a => [key(a.login, a.topicId), a]));
  const ranged = !!(from || to);
  const downloadCount = inRangeAcks.length + (inRangeHazards || []).length;
  // Said out loud on the document, because a filtered register that doesn't name its
  // period is the one thing an auditor cannot use.
  const topicLabel = topicId ? (published.find(t => t.id === topicId) || {}).title || '' : '';
  const rangeLabel = !ranged ? 'All acknowledgements to date'
    : from && to ? `Read between ${fmtDate(from)} and ${fmtDate(to)}`
      : from ? `Read on or after ${fmtDate(from)}`
        : `Read on or before ${fmtDate(to)}`;

  // One row per acknowledgement — the flat shape both exporters take. Sorted the way
  // the evidence is read: by topic, then by the person's name.
  const exportRows = () => inRangeAcks.map(a => ({
    topic: titleOf(a),
    emoji: emojiOf(a),
    login: a.login,
    name: who(a.login).name || '',
    area: who(a.login).area || '',
    lang: langLabel(a.lang),
    first: fmtWhen(a.first),
    last: fmtWhen(a.last),
    count: a.count || 1,
  })).sort((x, y) => x.topic.localeCompare(y.topic) || (x.name || x.login).localeCompare(y.name || y.login));

  const exportHazardRows = () => (inRangeHazards === null ? null : [...inRangeHazards]
    .sort((a, b) => (b.last || '').localeCompare(a.last || ''))
    .map(a => ({
      login: a.login,
      name: who(a.login).name || '',
      area: who(a.login).area || '',
      address: a.address || 'General hazard',
      action: a.action || '',
      lang: langLabel(a.lang),
      last: fmtWhen(a.last) + (a.count > 1 ? ` · ${a.count}×` : ''),
    })));

  // The list view is the download on screen: one row per acknowledgement, newest
  // first, so "who read this, and when" can be answered without opening a file.
  const listRows = [...inRangeAcks].sort((a, b) => (b.last || '').localeCompare(a.last || ''));

  const download = async (kind) => {
    setBusy(kind);
    try {
      const payload = {
        rows: exportRows(),
        hazardRows: exportHazardRows(),
        rangeLabel,
        scopeLabel: topicLabel,
        fileTag: [topicLabel.replace(/[^0-9a-zA-Z]+/g, '-').replace(/^-|-$/g, ''), ranged ? `${from || 'start'}_${to || 'today'}` : '']
          .filter(Boolean).join('_'),
        generatedAt: `Generated ${new Date().toLocaleString('en-AU', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`,
      };
      if (kind === 'xlsx') await downloadAwarenessXlsx(payload);
      else await downloadAwarenessPdf(payload);
    } catch (e) {
      notify?.(`Could not build the ${kind === 'xlsx' ? 'workbook' : 'PDF'}: ${e.message}`, 'error');
    }
    setBusy('');
  };

  if (!acks) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--hp-dim)' }}><Loader size={18} /> Loading…</div>;

  if (!logins.length && !(hazardAcks || []).length) {
    return <div className="card" style={{ padding: 30, textAlign: 'center', color: 'var(--hp-dim)' }}>
      Nobody has acknowledged a topic yet. Acknowledgements appear the moment a driver taps “Got it”.
    </div>;
  }

  return (
    <div>
      <div className="card" style={{ padding: 14, marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <Users size={16} color="var(--hp-gold)" />
        <div style={{ fontSize: '.83rem', color: 'var(--hp-dim)', flex: 1, minWidth: 260 }}>
          Who has been briefed on what, and in which language. This replaces the signed paper toolbox sheet — it is the
          evidence a contractor safety review asks for.
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 4, marginRight: 4 }}>
            <button className={`btn btn-sm ${view === 'grid' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setView('grid')} title="Every driver against every topic">
              <LayoutGrid size={14} /> Grid
            </button>
            <button className={`btn btn-sm ${view === 'list' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setView('list')} title="One row per acknowledgement, as the download reads">
              <List size={14} /> List
            </button>
          </div>
          <div>
            <label style={{ ...labelStyle, marginBottom: 2 }}>Topic</label>
            <select className="input" value={topicId} onChange={e => setTopicId(e.target.value)}
              style={{ fontSize: '.78rem', padding: '4px 6px', maxWidth: 200 }}>
              <option value="">All topics</option>
              {published.map(t => <option key={t.id} value={t.id}>{t.emoji} {t.title}</option>)}
            </select>
          </div>
          <div>
            <label style={{ ...labelStyle, marginBottom: 2 }}>From</label>
            <input type="date" className="input" value={from} max={to || undefined}
              onChange={e => setFrom(e.target.value)} style={{ fontSize: '.78rem', padding: '4px 6px' }} />
          </div>
          <div>
            <label style={{ ...labelStyle, marginBottom: 2 }}>To</label>
            <input type="date" className="input" value={to} min={from || undefined}
              onChange={e => setTo(e.target.value)} style={{ fontSize: '.78rem', padding: '4px 6px' }} />
          </div>
          {(ranged || topicId) && (
            <button className="btn btn-secondary btn-sm" onClick={() => { setFrom(''); setTo(''); setTopicId(''); }}
              title="Clear the filters">
              <X size={13} />
            </button>
          )}
          <button className="btn btn-secondary btn-sm" disabled={!!busy || !downloadCount} onClick={() => download('xlsx')}
            title="Download the register as an Excel workbook">
            {busy === 'xlsx' ? <Loader size={14} /> : <FileSpreadsheet size={14} />} Excel
          </button>
          <button className="btn btn-secondary btn-sm" disabled={!!busy || !downloadCount} onClick={() => download('pdf')}
            title="Download the register as a PDF">
            {busy === 'pdf' ? <Loader size={14} /> : <FileText size={14} />} PDF
          </button>
        </div>
        {/* What is on screen and what the buttons will produce — the same number. */}
        <div style={{ flexBasis: '100%', fontSize: '.72rem', color: 'var(--hp-dim)', textAlign: 'right' }}>
          {topicLabel ? `${topicLabel} · ` : ''}{rangeLabel} · <strong style={{ color: downloadCount ? 'var(--hp-text)' : '#ef4444' }}>{downloadCount}</strong>
          {' '}of {(acks || []).length + (hazardAcks || []).length} acknowledgement{downloadCount === 1 ? '' : 's'} shown, and in the download
        </div>
      </div>

      {view === 'list' ? (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
            <thead>
              <tr>
                <th style={th}>Safety topic</th>
                <th style={th}>Login</th>
                <th style={th}>Name</th>
                <th style={th}>Delivery area</th>
                <th style={th}>Language</th>
                <th style={th}>Read (date &amp; time)</th>
                <th style={{ ...th, textAlign: 'right' }}>Times</th>
              </tr>
            </thead>
            <tbody>
              {!listRows.length ? (
                <tr><td style={{ ...td, color: 'var(--hp-dim)' }} colSpan={7}>
                  {ranged ? 'No topic was acknowledged in this period.' : 'Nobody has acknowledged a topic yet.'}
                </td></tr>
              ) : listRows.map((a, i) => (
                <tr key={`${a.login}|${a.topicId}|${i}`}>
                  <td style={td}>
                    <span style={{ fontSize: '1.05rem', marginRight: 6 }}>{emojiOf(a)}</span>
                    {titleOf(a)}
                  </td>
                  <td style={{ ...td, fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{a.login}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{who(a.login).name || <span style={{ color: 'var(--hp-dim)', fontWeight: 400 }}>—</span>}</td>
                  <td style={td}>{who(a.login).area || <span style={{ color: 'var(--hp-dim)' }}>—</span>}</td>
                  <td style={td}>{langName(a.lang)} <span style={{ fontSize: '.72rem', color: 'var(--hp-dim)' }}>{langLabel(a.lang)}</span></td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {fmtWhen(a.last)}
                    {/* First read only earns a line when it differs — otherwise it is noise. */}
                    {a.first && a.first !== a.last && (
                      <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)' }}>first {fmtWhen(a.first)}</div>
                    )}
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>{a.count || 1}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
          <thead>
            <tr>
              <th style={{ ...th, position: 'sticky', left: 0, background: 'var(--hp-surface)' }}>Driver</th>
              {/* The emoji is how a driver recognises the brief on their phone, so it has to
                  read at a glance here too — the header's own .66rem would shrink it away. */}
              {shown.map(t => (
                <th key={t.id} style={{ ...th, textAlign: 'center' }}>
                  <span style={{ display: 'block', fontSize: '1.2rem', lineHeight: 1.4 }}>{t.emoji}</span>
                  {t.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {logins.map(l => (
              <tr key={l}>
                <td style={{ ...td, position: 'sticky', left: 0, background: 'var(--hp-surface)' }}>
                  <div style={{ fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{l}</div>
                  {(who(l).name || who(l).area) && (
                    <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)' }}>
                      {who(l).name}{who(l).name && who(l).area ? ' · ' : ''}{who(l).area}
                    </div>
                  )}
                </td>
                {shown.map(t => {
                  const a = map[key(l, t.id)];
                  return (
                    <td key={t.id} style={{ ...td, textAlign: 'center' }}>
                      {a ? (
                        <span title={`${fmtWhen(a.last)}${a.count > 1 ? ` · ${a.count}×` : ''}`}>
                          <CheckCircle size={14} color="#16a34a" /> <span style={{ fontSize: '.7rem' }}>{langName(a.lang)}</span>
                        </span>
                      ) : <span style={{ color: 'var(--hp-dim)' }}>—</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* Hazard acknowledgements — WHO was told about WHICH address, and when. This is
          the one an insurer or a WHS inspector actually asks for after an incident.
          A topic filter puts them out of scope, so the section goes rather than lying. */}
      {inRangeHazards && <div style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <MapPin size={15} color="var(--hp-gold)" />
          <div style={{ fontWeight: 800 }}>Hazard acknowledgements</div>
          <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)' }}>
            Every address a driver has tapped through, kept even after the hazard is cleared
          </div>
        </div>
        {!inRangeHazards.length ? (
          <div className="card" style={{ padding: 22, textAlign: 'center', color: 'var(--hp-dim)', fontSize: '.85rem' }}>
            {ranged ? 'No address hazard was acknowledged in this period.' : 'No address hazards have been acknowledged yet.'}
          </div>
        ) : (
          <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
              <thead><tr><th style={th}>Driver</th><th style={th}>Address</th><th style={th}>Instruction</th><th style={th}>Read</th><th style={th}>Lang</th></tr></thead>
              <tbody>
                {[...inRangeHazards].sort((a, b) => (b.last || '').localeCompare(a.last || '')).map((a, i) => (
                  <tr key={i}>
                    <td style={td}>
                      <div style={{ fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{a.login}</div>
                      {(who(a.login).name || who(a.login).area) && (
                        <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)' }}>
                          {who(a.login).name}{who(a.login).name && who(a.login).area ? ' · ' : ''}{who(a.login).area}
                        </div>
                      )}
                    </td>
                    <td style={td}>{a.address || <span style={{ color: 'var(--hp-dim)' }}>General hazard</span>}</td>
                    <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: '.72rem' }}>{a.action}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtWhen(a.last)}{a.count > 1 ? ` · ${a.count}×` : ''}</td>
                    <td style={td}>{langName(a.lang)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>}
    </div>
  );
}

// ── Modal shell ───────────────────────────────────────────────────────────────
function Modal({ title, children, onClose, wide, zIndex }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: zIndex || 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} className="card"
        style={{ width: '100%', maxWidth: wide ? 900 : 620, padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <div style={{ fontWeight: 800, fontSize: '1.05rem', flex: 1 }}>{title}</div>
          <button className="btn btn-secondary btn-sm" onClick={onClose}><X size={14} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
